import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Check, Search, Shield, X } from "lucide-react";
import { useEffect, useState } from "react";

interface PerfilAcesso {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
}

interface MenuSistema {
  id: number;
  nome: string;
  rota?: string;
  icone?: string;
  ordem: number;
  visivel: boolean;
  habilitar_para_paciente?: boolean;
}

interface ControleAcesso {
  id: number;
  id_perfil_acesso: number;
  id_menu: number;
  visualizar: boolean;
  criar: boolean;
  editar: boolean;
  excluir: boolean;
  relatorio: boolean;
  perfil?: PerfilAcesso;
  menu?: MenuSistema;
}

const ControleAcessoPage = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null);

  const [perfis, setPerfis] = useState<PerfilAcesso[]>([]);
  const [menus, setMenus] = useState<MenuSistema[]>([]);
  const [controlesAcesso, setControlesAcesso] = useState<ControleAcesso[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  // Carregar dados iniciais
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      // Carregar perfis de acesso (excluindo ADMIN e PACIENTE)
      const perfisResponse = await api.get('/api/perfil-acesso');
      if (perfisResponse?.result) {
        const perfisAtivos = perfisResponse.result.filter((p: PerfilAcesso) => {
          const nomeUpper = p.nome?.toUpperCase() || '';
          return p.ativo && nomeUpper !== 'ADMIN' && nomeUpper !== 'PACIENTE';
        });
        setPerfis(perfisAtivos);
        if (perfisAtivos.length > 0 && !selectedProfile) {
          setSelectedProfile(perfisAtivos[0].id);
        }
      }

      // Carregar menus do sistema (excluir menus habilitados apenas para paciente)
      const menusResponse = await api.get('/api/menu_sistema');
      if (menusResponse?.result) {
        const menusOrdenados = menusResponse.result
          .filter((m: MenuSistema) => m.visivel && !m.habilitar_para_paciente)
          .sort((a: MenuSistema, b: MenuSistema) => a.ordem - b.ordem);
        setMenus(menusOrdenados);
      }

      // Carregar controles de acesso
      const controlesResponse = await api.get('/api/controle-acesso');
      if (controlesResponse?.result) {
        setControlesAcesso(controlesResponse.result);
      }
    } catch (error: any) {
      console.error("Erro ao carregar dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados",
        variant: "destructive",
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Obter permissões para um perfil e menu específicos
  const getPermissoes = (perfilId: number, menuId: number): ControleAcesso | null => {
    return controlesAcesso.find(
      (ca) => ca.id_perfil_acesso === perfilId && ca.id_menu === menuId
    ) || null;
  };

  // Verificar se uma permissão está ativa
  const hasPermissao = (
    perfilId: number,
    menuId: number,
    tipo: 'visualizar' | 'criar' | 'editar' | 'excluir' | 'relatorio'
  ): boolean => {
    const controle = getPermissoes(perfilId, menuId);
    return controle ? controle[tipo] : false;
  };

  // Alternar permissão
  const togglePermissao = async (
    perfilId: number,
    menuId: number,
    tipo: 'visualizar' | 'criar' | 'editar' | 'excluir' | 'relatorio'
  ) => {
    if (!selectedProfile) {
      toast({
        title: "Erro",
        description: "Selecione um perfil primeiro",
        variant: "destructive",
      });
      return;
    }

    const controle = getPermissoes(perfilId, menuId);
    const key = `${perfilId}-${menuId}-${tipo}`;

    try {
      setSaving(prev => ({ ...prev, [key]: true }));

      const novoValor = !hasPermissao(perfilId, menuId, tipo);

      if (controle && controle.id) {
        // Atualizar controle existente, preservando todas as outras permissões
        const payload = {
          visualizar: tipo === 'visualizar' ? novoValor : controle.visualizar,
          criar: tipo === 'criar' ? novoValor : controle.criar,
          editar: tipo === 'editar' ? novoValor : controle.editar,
          excluir: tipo === 'excluir' ? novoValor : controle.excluir,
          relatorio: tipo === 'relatorio' ? novoValor : controle.relatorio,
        };

        console.log('Atualizando controle:', { id: controle.id, payload });
        await api.put(`/api/controle-acesso/${controle.id}`, payload);
      } else {
        // Criar novo controle - primeiro verificar se já existe
        const controleExistente = controlesAcesso.find(
          (ca) => ca.id_perfil_acesso === perfilId && ca.id_menu === menuId
        );

        if (controleExistente && controleExistente.id) {
          // Se encontrou um controle existente, atualizar ao invés de criar
          const payload = {
            visualizar: tipo === 'visualizar' ? novoValor : controleExistente.visualizar,
            criar: tipo === 'criar' ? novoValor : controleExistente.criar,
            editar: tipo === 'editar' ? novoValor : controleExistente.editar,
            excluir: tipo === 'excluir' ? novoValor : controleExistente.excluir,
            relatorio: tipo === 'relatorio' ? novoValor : controleExistente.relatorio,
          };

          console.log('Atualizando controle existente:', { id: controleExistente.id, payload });
          await api.put(`/api/controle-acesso/${controleExistente.id}`, payload);
        } else {
          // Criar novo controle
          const novoControle = {
            id_perfil_acesso: perfilId,
            id_menu: menuId,
            visualizar: tipo === 'visualizar' ? novoValor : false,
            criar: tipo === 'criar' ? novoValor : false,
            editar: tipo === 'editar' ? novoValor : false,
            excluir: tipo === 'excluir' ? novoValor : false,
            relatorio: tipo === 'relatorio' ? novoValor : false,
          };

          console.log('Criando novo controle:', novoControle);
          await api.post('/api/controle-acesso', novoControle);
        }
      }

      // Recarregar dados sem mostrar o loading global para não voltar o scroll
      await carregarDados(false);

      toast({
        title: "Sucesso",
        description: "Permissão atualizada com sucesso",
      });
    } catch (error: any) {
      console.error("Erro ao atualizar permissão:", error);
      console.error("Detalhes do erro:", {
        message: error.message,
        response: error.response,
        stack: error.stack
      });

      // Tentar extrair a mensagem de erro de diferentes formatos
      let errorMessage = "Não foi possível atualizar a permissão";

      if (error?.response?.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const filteredMenus = menus.filter((menu) =>
    menu.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const perfilSelecionado = perfis.find((p) => p.id === selectedProfile);
  const totalPermissoes = selectedProfile
    ? controlesAcesso.filter((ca) => ca.id_perfil_acesso === selectedProfile).length
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2 text-foreground">
              <Shield className="h-8 w-8 text-primary" />
              Controle de Acesso
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie as permissões de acesso aos módulos do sistema por perfil
            </p>
          </div>
          <Badge variant="outline" className="text-sm">
            {menus.length} módulos disponíveis
          </Badge>
        </div>

        {/* Seleção de Perfil */}
        <Card>
          <CardHeader>
            <CardTitle>Selecione o Perfil</CardTitle>
            <CardDescription>
              Escolha um perfil para configurar suas permissões de acesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {perfis.map((perfil) => (
                <Button
                  key={perfil.id}
                  variant={selectedProfile === perfil.id ? "default" : "outline"}
                  className="h-auto flex-col items-start p-4 text-left"
                  onClick={() => setSelectedProfile(perfil.id)}
                >
                  <div className="flex items-center gap-2 w-full mb-2">
                    <div className="h-3 w-3 rounded-full bg-primary" />
                    <span className="font-semibold">{perfil.nome}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {perfil.descricao || "Sem descrição"}
                  </span>
                  <Badge variant="secondary" className="mt-2">
                    {controlesAcesso.filter((ca) => ca.id_perfil_acesso === perfil.id).length} permissões
                  </Badge>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Módulos e Permissões */}
        {selectedProfile && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Permissões - {perfilSelecionado?.nome}</CardTitle>
                  <CardDescription>
                    Configure as permissões de acesso para cada módulo
                  </CardDescription>
                </div>
                <Badge className="bg-green-500">
                  <Check className="h-3 w-3 mr-1" />
                  {totalPermissoes} módulos configurados
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campo de busca */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar módulo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Lista de módulos com permissões */}
              <div className="space-y-4">
                {filteredMenus.map((menu) => {
                  const temVisualizar = hasPermissao(selectedProfile, menu.id, 'visualizar');
                  const temCriar = hasPermissao(selectedProfile, menu.id, 'criar');
                  const temEditar = hasPermissao(selectedProfile, menu.id, 'editar');
                  const temExcluir = hasPermissao(selectedProfile, menu.id, 'excluir');
                  const temRelatorio = hasPermissao(selectedProfile, menu.id, 'relatorio');

                  const temAlgumaPermissao = temVisualizar || temCriar || temEditar || temExcluir || temRelatorio;

                  return (
                    <div
                      key={menu.id}
                      className={`p-4 rounded-lg border-2 transition-all ${temAlgumaPermissao
                          ? "border-primary bg-primary/5"
                          : "border-border bg-card hover:border-primary/50"
                        }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="font-semibold text-foreground flex items-center gap-2 mb-1">
                            {menu.nome}
                            {temAlgumaPermissao && (
                              <Badge variant="default" className="text-xs">
                                <Check className="h-3 w-3 mr-1" />
                                Ativo
                              </Badge>
                            )}
                            {!temAlgumaPermissao && (
                              <Badge variant="secondary" className="text-xs">
                                <X className="h-3 w-3 mr-1" />
                                Sem permissões
                              </Badge>
                            )}
                          </div>
                          {menu.rota && (
                            <p className="text-sm text-muted-foreground">{menu.rota}</p>
                          )}
                        </div>
                      </div>

                      {/* Permissões CRUD e Relatórios */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="flex items-center justify-between p-3 rounded border bg-background">
                          <div>
                            <div className="text-sm font-medium">Visualizar</div>
                            <div className="text-xs text-muted-foreground">Ver conteúdo</div>
                          </div>
                          <Switch
                            checked={temVisualizar}
                            onCheckedChange={() => togglePermissao(selectedProfile, menu.id, 'visualizar')}
                            disabled={saving[`${selectedProfile}-${menu.id}-visualizar`]}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded border bg-background">
                          <div>
                            <div className="text-sm font-medium">Criar</div>
                            <div className="text-xs text-muted-foreground">Adicionar</div>
                          </div>
                          <Switch
                            checked={temCriar}
                            onCheckedChange={() => togglePermissao(selectedProfile, menu.id, 'criar')}
                            disabled={saving[`${selectedProfile}-${menu.id}-criar`]}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded border bg-background">
                          <div>
                            <div className="text-sm font-medium">Editar</div>
                            <div className="text-xs text-muted-foreground">Modificar</div>
                          </div>
                          <Switch
                            checked={temEditar}
                            onCheckedChange={() => togglePermissao(selectedProfile, menu.id, 'editar')}
                            disabled={saving[`${selectedProfile}-${menu.id}-editar`]}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded border bg-background">
                          <div>
                            <div className="text-sm font-medium">Excluir</div>
                            <div className="text-xs text-muted-foreground">Remover</div>
                          </div>
                          <Switch
                            checked={temExcluir}
                            onCheckedChange={() => togglePermissao(selectedProfile, menu.id, 'excluir')}
                            disabled={saving[`${selectedProfile}-${menu.id}-excluir`]}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded border bg-background">
                          <div>
                            <div className="text-sm font-medium">Relatório</div>
                            <div className="text-xs text-muted-foreground">Gerar relatórios</div>
                          </div>
                          <Switch
                            checked={temRelatorio}
                            onCheckedChange={() => togglePermissao(selectedProfile, menu.id, 'relatorio')}
                            disabled={saving[`${selectedProfile}-${menu.id}-relatorio`]}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredMenus.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum módulo encontrado</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!selectedProfile && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Selecione um perfil para configurar as permissões
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ControleAcessoPage;

